import { APIResponse } from "../../../models/NetworkResponse";
import {
  SwayyCategoryDetail,
  SwayyCategoryOptionLink,
  SwayyCategoryTreeNode,
  SwayyCreateCategoryPayload,
  SwayyUpdateCategoryPayload,
} from "../models/categories/SwayyCategory";
import { getAPI, patchAPI, postAPI, putAPI } from "./Networking";
import { mapSwayyCatalogOption } from "./SwayyOptionsAPI";
import {
  asBool,
  assertSuccess,
  getResponseData,
  pickNumber,
  pickString,
  SwayyApiError,
  SwayyAPIResponse,
} from "./swayyApiHelpers";

const BASE = "/v1/catalog/admin/categories";

export type {
  SwayyCategoryDetail,
  SwayyCategoryOptionLink,
  SwayyCategoryTreeNode,
  SwayyCreateCategoryPayload,
  SwayyUpdateCategoryPayload,
};

const mapTreeNode = (raw: unknown): SwayyCategoryTreeNode | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const childrenRaw = Array.isArray(row.children) ? row.children : [];
  return {
    id,
    parentId: pickNumber(row.parent_id),
    name: pickString(row.name) || `Category #${id}`,
    slug: pickString(row.slug) || "",
    skuCode: pickString(row.sku_code) || "",
    position: pickNumber(row.position) ?? 0,
    isActive: asBool(row.is_active, true),
    children: childrenRaw
      .map(mapTreeNode)
      .filter((node): node is SwayyCategoryTreeNode => node !== null),
  };
};

const mapCategoryDetail = (raw: unknown): SwayyCategoryDetail | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const optionRows = Array.isArray(row.category_options)
    ? row.category_options
    : Array.isArray(row.options)
      ? row.options
      : [];
  return {
    id,
    parentId: pickNumber(row.parent_id),
    name: pickString(row.name) || `Category #${id}`,
    slug: pickString(row.slug) || "",
    skuCode: pickString(row.sku_code) || "",
    position: pickNumber(row.position) ?? 0,
    isActive: asBool(row.is_active, true),
    options: optionRows
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const link = item as Record<string, unknown>;
        const optionId = Number(link.option_id ?? (link.option as { id?: number })?.id);
        if (!Number.isInteger(optionId) || optionId <= 0) return null;
        return {
          optionId,
          position: pickNumber(link.position) ?? 0,
          isRequired: asBool(link.is_required, false),
          option: mapSwayyCatalogOption(link.option ?? link),
        };
      })
      .filter((item): item is SwayyCategoryOptionLink => item !== null),
  };
};

export const listSwayyCategoryTree = async (
  signal?: AbortSignal,
): Promise<SwayyCategoryTreeNode[]> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/tree`,
    undefined,
    signal,
  );
  assertSuccess(response);
  const payload = getResponseData(response);
  const rows = Array.isArray(payload) ? payload : [];
  return rows
    .map(mapTreeNode)
    .filter((node): node is SwayyCategoryTreeNode => node !== null);
};

export const getSwayyCategory = async (
  categoryId: number,
  signal?: AbortSignal,
): Promise<SwayyCategoryDetail> => {
  const response = await getAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/${categoryId}`,
    undefined,
    signal,
  );
  assertSuccess(response);
  const mapped = mapCategoryDetail(getResponseData(response));
  if (!mapped) throw new SwayyApiError("Category not found.");
  return mapped;
};

export const createSwayyCategory = async (
  payload: SwayyCreateCategoryPayload,
): Promise<SwayyCategoryDetail> => {
  const response = await postAPI(BASE, payload);
  assertSuccess(response);
  const mapped = mapCategoryDetail(
    (response as APIResponse & { data?: unknown }).data,
  );
  if (!mapped) {
    throw new SwayyApiError("Category was created but response was empty.");
  }
  return mapped;
};

export const updateSwayyCategory = async (
  categoryId: number,
  payload: SwayyUpdateCategoryPayload,
): Promise<SwayyCategoryDetail> => {
  const response = await patchAPI<SwayyAPIResponse<unknown>>(
    `${BASE}/${categoryId}`,
    payload,
  );
  assertSuccess(response);
  const mapped = mapCategoryDetail(getResponseData(response));
  if (!mapped) {
    throw new SwayyApiError("Category was updated but response was empty.");
  }
  return mapped;
};

export const updateSwayyCategoryOptions = async (
  categoryId: number,
  options: Array<{ optionId: number; position: number; isRequired: boolean }>,
): Promise<SwayyCategoryDetail> => {
  const response = await putAPI(`${BASE}/${categoryId}/options`, {
    options: options.map((item) => ({
      option_id: item.optionId,
      position: item.position,
      is_required: item.isRequired,
    })),
  });
  assertSuccess(response);
  const mapped = mapCategoryDetail(
    (response as APIResponse & { data?: unknown }).data,
  );
  if (mapped) return mapped;
  return getSwayyCategory(categoryId);
};

export const flattenCategoryTree = (
  nodes: SwayyCategoryTreeNode[],
  depth = 0,
): Array<SwayyCategoryTreeNode & { depth: number }> =>
  nodes.flatMap((node) => [
    { ...node, depth },
    ...flattenCategoryTree(node.children, depth + 1),
  ]);
